import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button, DuplicateOutline, Flex, rem, Text } from '@bonusly/design-system';
import { EventEntity } from '@bonusly/one-on-ones-entities';

import { useCurrentUser } from '../../../../../lib/who-am-i';

type EventDetailsProps = {
	// NOTE: In a lot of places I've seen us passing individual values like `eventId`, `guestId`, `scheduledTime` to components. There's nothign wrong with that,
	// but if you're going to use a ton of properties from an entity just pass that in instead! It all comes down to how or if this component is going to be shared.
	// If it's only ever going to be used in this package, go with passing entities around. If it's going to be global and work with more than one entity, then
	// you can even `extend` a certain set of attributes. Or, if it's going to work with a lot of different data types that aren't related to entities, just pass in
	// the individual properties.
	event: EventEntity;
	onClose: () => void;
};

// NOTE: This is another shared component that should always behave the same. It was conditionally shown previously based on the presence or absence of an event.
// https://gitlab.com/bonusly/engineering/front-end/kaleidoscope/-/blob/e9b53010a0e2dd2a9ccf8f030790a6b278f0aa97/packages/common/meeting-modal/src/components/meeting/modal/meeting-modal-body.tsx
// Now it always expects an event to be passed in and will be displayed as needed.
export const EventDetails: FC<EventDetailsProps> = ({ event, onClose }) => {
	const { t } = useTranslation('one_on_ones');
	const currentUser = useCurrentUser();
	const isManager = event.managerId == currentUser.id;

	// link to the last/current meeting for this event.
	// Use /growth/meetings/:id - works for V1 users directly, Bonusly users get redirected to /meet/:id
	const meetingPath = isManager ? `/team/${event.guestId}/meet` : `/growth/meetings/${event.id}`;
	const meetingUrl = `${window.location.origin}${meetingPath}`;

	const handleCopy = () => {
		navigator.clipboard.writeText(meetingUrl);
	};

	return (
		<>
			<Flex direction="column" gap={1}>
				<Text color="text.default" fontWeight="medium">
					{t('meeting.modal.meeting_url', { ns: 'one_on_ones' })}
				</Text>
				<Flex
					justifyContent="space-between"
					px={4}
					py={rem(10)}
					borderColor="border.default !important"
					border="1px solid"
					borderRadius={8}
					alignItems="center"
					gap={2}
					overflow="hidden"
				>
					<Text color="text.default" fontSize={100} fontWeight="normal" isTruncated flex={1} minW={0}>
						{meetingUrl}
					</Text>
					<DuplicateOutline
						onClick={handleCopy}
						_active={{ color: 'gray.100', transform: 'scale(0.95)' }}
						color="text.interactive"
						cursor="pointer"
						flexShrink={0}
					/>
				</Flex>
			</Flex>

			<Button as={Link} colorScheme="primary" pointer="cursor" to={meetingPath} variant="alpha" w="full" onClick={onClose}>
				{t('meeting.modal.goto_meeting')}
			</Button>
		</>
	);
};
